<script setup>
import axios from "@axios"
import {useAppAbility} from "@/plugins/casl/useAppAbility"
import { useDesignSystem } from '@/composables/useDesignSystem'

import {useRoute} from "vue-router"
const emit = defineEmits([
  'submit',
])

const { dialogWidth } = useDesignSystem()
const dWidth = dialogWidth('md')

const route = useRoute()
const router = useRouter()

// Ability
const ability = useAppAbility()


import {useUsersStore} from '@/stores'
const usersStore = useUsersStore()
let myUser = null
let isShowingCodeConfirmationDialog = null;
({ myUser, isShowingCodeConfirmationDialog } = storeToRefs(usersStore))

const code = ref('')
const firstName = ref(myUser.value.firstName || '')
const lastName = ref(myUser.value.lastName || '')
const referredBy = ref(myUser.value.primaryGroupName || myUser.value.referredBy )
const disableReferredBy = ref(false)
const confirming = ref(false)

watch(() => myUser.value, (currentValue, oldValue) => {
  const newReferredBy = currentValue.primaryGroupName || currentValue.referredBy

  if (newReferredBy !== referredBy.value) {
    referredBy.value = newReferredBy

    // Disable the referredBy field if referredBy has a value
    if (referredBy.value) {
      disableReferredBy.value = true
    }
  }
}, {
  // Additional options, if needed.
  // For example, you can use { deep: true } if myUser.value is a nested object and you want to watch its nested properties.
})

const newCodeRequested = ref(false)

// Form Errors
const errors = ref({
  email: undefined,
  password: undefined,
  termsAccepted: undefined
})

const checkCode = () => {
  console.log("Sending /auth/verify-email")
  confirming.value = true
  axios.post('/auth/verify-email', {
    email: JSON.parse(localStorage.getItem('userData')).email,
    code: code.value,
    firstName: firstName.value.trim(),
    lastName: lastName.value.trim(),
    referredBy: referredBy.value,
    submitForApproval: myUser.value.submitForApproval
  }, {
    params:{
    }
  }).then(async r => {
    const {user, userAbilities, accessToken, jwt} = r.data
    localStorage.setItem('userAbilities', JSON.stringify(userAbilities))
    localStorage.setItem('accessToken', JSON.stringify(jwt))
    localStorage.setItem('jwt', JSON.stringify(jwt))
    ability.update(userAbilities)
    localStorage.setItem('userData', JSON.stringify(user))
    await usersStore.getMyUser()

    //the below using to be a location.href - that may have been fixing a problem by causing a full refresh
    isShowingCodeConfirmationDialog.value = false

    // New member onboarding: if the freshly-verified user hasn't yet
    // accepted the Member Commitment, show the gate with source=onboarding
    // (overrides the default existing_member_gate set by App.vue's
    // subscriber). RM-140 Phase 3.
    if (!usersStore.myUser?.memberCommitment?.accepted) {
      await usersStore.showMemberCommitmentDialog({ mode: 'gate', source: 'onboarding' })
    }

    router.push({ path: '/m/' })

  }).catch(e => {
    console.error(e)
    const {errors: formErrors} = e.response.data
    errors.value = formErrors
    console.error(e.response.data)
    confirming.value = false
  })
}

const resendCode = () => {
  console.log("Sending /auth/verify-email")
  axios.post('/auth/resend-verify-email-code', {
    email: JSON.parse(localStorage.getItem('userData')).email
  }, {
    params:{
    }
  }).then(r => {
    newCodeRequested.value = true
    
    return null
  }).catch(e => {
    console.error(e)
    const {errors: formErrors} = e.response.data
    errors.value = formErrors
    console.error(e.response.data)
  })
}
const logOut = () => {
  localStorage.removeItem('userData')
  localStorage.removeItem('accessToken')
  localStorage.removeItem('jwt')
  location.href="/auth/logout?loc=2"
}
</script>

<template>
  <VDialog
    :width="dWidth"
    :model-value="isShowingCodeConfirmationDialog"
    persistent
  >
    <VCard class="pa-3 pa-sm-15 ma-0">
      <!-- 👉 Title -->
      <VCardItem class="text-center pa-0">
        <VCardTitle class="text-h5 font-weight-medium mb-2">
          Welcome to RM!
        </VCardTitle>
        <VCardText
          v-if="!newCodeRequested"
          class="pr-0 pl-0"
        >
          We sent a verification code to<br><span>{{ myUser.email }}</span>
        </VCardText>

        <VCardText v-else>
          New code resent to<br><span class="text-lg"><b>{{ myUser.email }}</b></span><br><br>Code sent from preferred@reserverm.com<br>If you didn’t receive, please check spam or use alternate email
        </VCardText>
      </VCardItem>

      <VCardText class="mt-0">
        <VForm @submit.prevent="() => {}">
          <VRow>
            <!-- 👉 Card Number -->
            <VCol cols="12">
              <VTextField
                v-model="code"
                label="Confirmation Code"
                type="number"
                :error-messages="errors.code"
                autofocus
                style="margin-bottom: 0px !important;"
              />
            </VCol>
            <VExpandTransition name="expand-transition">
              <VCol
                v-if="code >= 100000"
                cols="12"
              >
                <VTextField
                  v-model="firstName"
                  label="First Name"
                  type="text"
                  style="margin-bottom: 0px !important;"
                />
                <br>
                <VTextField
                  v-model="lastName"
                  label="Last Name"
                  type="text"
                  style="margin-bottom: 0px !important;"
                />
                <br>
                <VTextField
                  v-if="!myUser.verificationInfo1Label"
                  v-model="referredBy"
                  label="Who referred you?"
                  type="text"
                  hint="Optional"
                  :disabled="disableReferredBy"
                  style="margin-bottom: 0px !important;"
                />
              </VCol>
            </VExpandTransition>


            <!-- 👉 Card actions -->
            <VCol
              cols="12"
              class="d-flex flex-wrap justify-center gap-4"
            >
              <VBtn
                v-if="myUser?.primaryGroupPermission == 'pending'"
                type="submit"
                :disabled="!code || !firstName || !lastName"
                @click=" myUser.submitForApproval = true; checkCode();"
              >
                Submit for Approval
              </VBtn>
              <VBtn
                v-else
                type="submit"
                :disabled="!code || !firstName || !lastName || confirming"
                @click="checkCode"
              >
                Submit
              </VBtn>
            </VCol>
            <VCol
              cols="12"
              class="d-flex flex-wrap justify-center gap-4"
            >
              <VBtn
                color="secondary"
                variant="tonal"
                @click="resendCode"
              >
                Resend Code
              </VBtn>
              <VBtn
                color="secondary"
                variant="tonal"
                @click="logOut"
              >
                Log Out
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>
  </VDialog>
</template>


